/**
 * The button that starts the game.
 *
 * Beside the lobby code, because that is the moment: five people have a code
 * and a server and are all about to go and find the same launcher in their own
 * taskbar. One press opens it.
 *
 * The whole difficulty is that "the game" is not one program. Valorant is the
 * Riot client, Apex is Steam or EA or Epic depending on where you bought it,
 * and opening the wrong one is worse than opening nothing - it starts a
 * 200MB launcher that then tells you the game is not installed. So:
 *
 *   - the first press for a game asks which store you own it on, once
 *   - the answer is kept on your account, so it follows you to another machine
 *   - the address is built on the server from a fixed table, never here
 *   - only the desktop app can open one, because a protocol address is a
 *     command to the operating system and a web page does not get to send one
 *
 * In a browser the button still appears and says so, rather than silently
 * doing nothing: knowing the feature exists and needs the app is more useful
 * than a button that shrugs.
 */

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { bridge } from '@/lib/bridge';
import { cn } from '@/lib/cn';
import { useSession } from '@/stores/session';
import { useIsPlaying } from '@/lib/playing';
import { Icon } from '@/icons/Icon';
import { GameMark } from './GameMark';
import { Button, Modal, useConfirm, useToast } from './ui';

type Store = { id: string; name: string; kind: 'launch' | 'store' };
type Launch = {
  gameId: string;
  stores: Store[];
  chosen: string | null;
  launch: { url: string; store: string; storeName: string; kind: 'launch' | 'store' } | null;
};

type Installed = {
  gameId: string;
  chosenPath: string | null;
  found: boolean;
  how: 'chosen' | 'steam' | 'installed' | null;
  file: string | null;
  searchable: boolean;
};

export function OpenGame({ gameId, className }: { gameId: string; className?: string }) {
  const catalog = useSession((s) => s.catalog);
  const queryClient = useQueryClient();
  const toast = useToast();
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  /* Whether this one is open right now, from the shell's own watch. */
  const playing = useIsPlaying(gameId);
  const { confirm, dialog: confirmDialog } = useConfirm();
  /*
   * Whether the game is on this machine.
   *
   * Asked once when the button appears, because it is a handful of stat
   * calls and the answer decides what the button says. Not polled: games do
   * not install themselves while you are looking at a lobby code.
   */
  const [installed, setInstalled] = useState<Installed | null>(null);
  useEffect(() => {
    let gone = false;
    const desk = bridge();
    if (!desk?.game?.find) return;
    void desk.game.find(gameId).then((r) => { if (!gone) setInstalled(r); }).catch(() => { /* not fatal */ });
    return () => { gone = true; };
  }, [gameId]);

  /* Which stores this game is on at all. Straight off the catalogue, so a
     game Pugo has no launch address for never draws a button. */
  const known = (catalog as unknown as { launch?: { games: Record<string, string[]> } })?.launch?.games?.[gameId];

  const q = useQuery({
    queryKey: ['launch', gameId],
    queryFn: () => api.get<Launch>('/api/launch/' + encodeURIComponent(gameId)),
    enabled: !!known?.length,
    staleTime: 5 * 60_000
  });

  if (!known?.length) return null;
  const state = q.data ?? null;
  const game = catalog?.games.find((g) => g.id === gameId);
  const desk = bridge();

  /**
   * Start it.
   *
   * The installed copy first, because that actually starts the game. The
   * store protocol second, which asks a launcher to do it. A web page last,
   * and only when the entry really is a page.
   */
  async function open(url: string | null, kind: 'launch' | 'store') {
    if (!desk?.game) {
      if (url && kind === 'store' && /^https:/i.test(url)) { window.open(url, '_blank', 'noopener'); return; }
      toast('Opening a game needs the Pugo desktop app', 'warn');
      return;
    }

    setBusy(true);
    try {
      if (installed?.found && desk.game.run) {
        const ran = await desk.game.run(gameId);
        if (ran.ok) return;
        /* It was there a moment ago and is not now - fall through to the
           store rather than stopping with an error nobody can act on. */
      }

      if (!url) {
        setChoosing(true);
        return;
      }
      if (kind === 'store' && /^https:/i.test(url)) { void desk.openExternal(url); return; }

      const res = await desk.game.launch(url);
      if (!res.ok) {
        toast(res.reason === 'too-soon' ? 'One at a time' : 'That launcher could not be opened', 'bad');
      }
    } finally {
      setBusy(false);
    }
  }

  /** Point Pugo at a copy it could not find. */
  async function pick() {
    if (!desk?.game?.pick) return;
    const res = await desk.game.pick(gameId);
    if (res.ok) {
      const next = await desk.game.find(gameId);
      setInstalled(next);
      setChoosing(false);
      toast('Saved. That is what Pugo will run.', 'good');
    } else if (res.reason === 'not-runnable') {
      toast('That is not something Pugo can run.', 'bad');
    }
  }

  async function choose(store: string | null) {
    try {
      const res = await api.post<{ launch: Launch }>('/api/me/owns', { gameId, store });
      queryClient.setQueryData(['launch', gameId], res.launch);
      setChoosing(false);
      if (store && res.launch.launch) await open(res.launch.launch.url, res.launch.launch.kind);
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  const ready = state?.launch ?? null;

  /*
   * Already open.
   *
   * The desktop shell watches for this anyway - it is what tells your friends
   * what you are playing - so the button can stop offering to start something
   * that is already in front of you and offer to close it instead.
   */
  async function end() {
    if (!desk?.game?.stop) return;
    const sure = await confirm(
      'Close ' + (game?.name ?? 'the game') + '?',
      'It will be shut down straight away. Anything unsaved in it is lost.',
      { confirmLabel: 'Close it', danger: true }
    );
    if (!sure) return;
    setBusy(true);
    try {
      const res = await desk.game.stop(gameId);
      if (res.ok) toast((game?.name ?? 'The game') + ' closed', 'good');
      else if (res.reason === 'not-running') toast('It is not running any more', 'warn');
      else if (res.reason === 'too-soon') toast('One at a time', 'warn');
      else toast('That one would not close', 'bad');
    } finally {
      setBusy(false);
    }
  }
  /* What the button should say. Finding it beats being told where it was
     bought, because one of those starts the game. */
  const label = busy ? (playing ? 'Closing…' : 'Opening…')
    : playing ? 'End ' + (game?.name ?? 'game')
      : installed?.found ? 'Play ' + (game?.name ?? 'game')
        : ready ? (ready.kind === 'store' ? 'Open store page' : 'Open ' + (game?.name ?? 'game'))
          : 'Open game';

  return (
    <>
      {confirmDialog}
      <div className={cn('flex items-center gap-1.5', className)}>
        <button
          type="button"
          disabled={busy || q.isPending}
          onClick={() => {
            if (playing) { void end(); return; }
            if (installed?.found || ready) { void open(ready?.url ?? null, ready?.kind ?? 'launch'); return; }
            setChoosing(true);
          }}
          className={cn(
            'flex h-[34px] min-w-0 flex-1 items-center justify-center gap-2 rounded-[calc(12px*var(--radius-mul,1))]',
            'px-3 text-[13px] font-bold outline-none transition-[background-color] duration-150',
            'shadow-[inset_0_1px_0_rgba(255,255,255,.18)] disabled:opacity-60',
            /* Running: the same button, in the colour of a thing that stops
               rather than starts. Nothing moves, so the row does not jump
               when a game you started comes up. */
            playing
              ? 'bg-[color-mix(in_srgb,var(--bad)_88%,transparent)] text-white hover:bg-bad focus-visible:shadow-[0_0_0_2px_color-mix(in_srgb,var(--bad)_40%,transparent)]'
              : 'bg-[linear-gradient(180deg,var(--accent-hi),var(--accent))] text-accent-fg hover:bg-accent-hi focus-visible:shadow-[0_0_0_2px_var(--accent-soft)]'
          )}
        >
          <Icon name={playing ? 'close' : 'play'} size={14} />
          <span className="truncate">{label}</span>
        </button>
        {ready || installed?.found ? (
          <button
            type="button"
            aria-label="Change how this game opens"
            title={installed?.found
              ? 'Runs ' + (installed.file ?? 'the installed copy')
              : 'Opens through ' + (ready?.storeName ?? 'the store')}
            onClick={() => setChoosing(true)}
            className="grid h-[34px] w-[34px] flex-none place-items-center rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 text-mute outline-none transition-colors hover:bg-surface-3 hover:text-ink"
          >
            <Icon name="sliders" size={14} />
          </button>
        ) : null}
      </div>

      {choosing ? (
        <Modal
          title={'Where do you own ' + (game?.name ?? 'this game') + '?'}
          subtitle="Asked once. Pugo opens that launcher from then on, on any machine you sign in to."
          onClose={() => setChoosing(false)}
          footer={
            <>
              {state?.chosen ? (
                <Button kind="ghost" onClick={() => void choose(null)}>Forget this</Button>
              ) : null}
              <span className="flex-1" />
              <Button kind="ghost" onClick={() => setChoosing(false)}>Cancel</Button>
            </>
          }
        >
          <div className="flex items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3">
            <GameMark id={gameId} size={34} />
            <span className="min-w-0">
              <span className="block truncate text-[14px] font-bold text-ink">{game?.name ?? gameId}</span>
              <span className="block text-[12px] text-mute">{game?.genre ?? 'Game'}</span>
            </span>
          </div>

          {desk?.game?.find ? (
            <div className="mt-3 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-3">
              <div className="flex items-center gap-2.5">
                <Icon name={installed?.found ? 'checkCirc' : 'search'} size={16} className={installed?.found ? 'flex-none text-good' : 'flex-none text-mute'} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-bold text-ink">
                    {installed?.found ? 'Found on this machine' : 'Not found on this machine'}
                  </span>
                  <span className="block truncate text-[11.5px] text-mute">
                    {installed?.found
                      ? (installed.how === 'chosen' ? 'You pointed Pugo at this one' : installed.how === 'steam' ? 'From your Steam library' : 'From where it installed')
                      : installed?.searchable ? 'Pugo looked in your Steam libraries and the usual folders.' : 'Pugo does not know where this one installs.'}
                  </span>
                </span>
                <Button size="sm" kind="outline" onClick={() => void pick()}>
                  {installed?.chosenPath ? 'Change' : 'Point me at it'}
                </Button>
              </div>
              {installed?.file ? (
                <p className="selectable m-0 mt-2 truncate font-mono text-[11px] text-ghost" title={installed.file}>{installed.file}</p>
              ) : null}
            </div>
          ) : null}

          <p className="mt-4 mb-1.5 text-[11.5px] font-semibold tracking-[.06em] text-ghost uppercase">Or open it through a store</p>
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {(state?.stores ?? []).map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => void choose(s.id)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-3 py-2.5 text-left outline-none transition-colors hover:bg-surface-3',
                    state?.chosen === s.id && 'shadow-[inset_0_0_0_2px_var(--accent)]'
                  )}
                >
                  <Icon name={s.kind === 'launch' ? 'play' : 'external'} size={16} className="flex-none text-mute" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13.5px] font-bold text-ink">{s.name}</span>
                    <span className="block text-[11.5px] text-mute">
                      {s.kind === 'launch' ? 'Starts the game through this launcher' : 'Opens its page - no launcher to start it with'}
                    </span>
                  </span>
                  {state?.chosen === s.id ? <Icon name="check" size={15} className="flex-none text-accent" /> : null}
                </button>
              </li>
            ))}
          </ul>

          <p className="mt-3 mb-0 text-[11.5px] leading-relaxed text-ghost">
            {desk?.game
              ? 'Pugo hands the address to your launcher and nothing else - it never looks at what you own.'
              : 'Opening a launcher needs the Pugo desktop app. In a browser only store pages can open.'}
          </p>
        </Modal>
      ) : null}
    </>
  );
}
